/**
 * Prompt Builder Analysis Schema
 *
 * JSON Schema for LLM structured output in prompt builder.
 * Supports all 24 toggles with detailed analysis fields.
 *
 * This schema is used by the prompt builder preview endpoint to generate
 * comprehensive analysis results based on active toggles.
 */

import { PURCHASE_INTENT_CATEGORIES } from '@aica/api-types';

import { OBJECTION_CODES } from '../voc/objection-codes';

/**
 * Fixed codes for `mandatory_phrases.missing_codes` (analysis-metric-semantics,
 * Task 3). The model used to describe a missing requirement in free text,
 * re-translated into the analysis language on every call — the same
 * requirement came back in six different wordings across real calls, so it
 * could not be grouped, counted, or targeted by a rule. These codes are the
 * single source of truth for the enum below, for the prompt guideline in
 * `@aica/prompt-builder` (`analysis-guidelines.ts`), and for the i18n labels
 * in `packages/core/src/i18n/` — all three must stay in sync with this list.
 *
 * Since Э3 (#2846) the main analysis no longer writes `mandatory_phrases`: the
 * built-in criteria own those checks. The list stays only because analyses stored
 * before Э3 still carry these codes and the panels render their labels
 * (`mandatoryPhraseCodeLabel.ts`).
 */
export const MANDATORY_PHRASE_CODES = [
  'RECORDING_DISCLAIMER',
  'COMPANY_NAME',
  'CALLBACK_OPTION',
  'SATISFACTION_GUARANTEE',
] as const;

export type MandatoryPhraseCode = (typeof MANDATORY_PHRASE_CODES)[number];

/**
 * Every array here carries `maxItems`, and that is a budget guard, not style.
 *
 * Under `strict: true` the decoder is grammar-constrained, so "one more array
 * element" stays a legal continuation for as long as the model wants it: the
 * only thing that ever stopped a runaway was `max_output_tokens`. Production
 * showed what that costs — 79 analyses between 2026-08-17 and 2026-09-07 ended
 * at `finish_reason=length` with `tokens_output` exactly at the 32k cap and
 * `content` up to 719 994 characters, against a normal analysis of 5-10 KB
 * (#657). Each one burned a full paid call and fell through to the salvage
 * retry. A bound makes that generation ungrammatical rather than merely
 * unwanted.
 *
 * `safety-gates.ts` already clamps `stop_words` to 20
 * entries after the fact ("the mini loop produced ~100"). That trim keeps the
 * stored analysis sane but arrives after the tokens are spent — same disease,
 * later layer. These caps are the earlier layer, so both stay.
 *
 * Bounds are measured, not guessed: over 7497 production analyses (30 days) the
 * observed maxima were <= 11 for every array except `profanity_detection.
 * instances` (127, p99 47; the field left the schema in Э3). Each cap sits at or
 * above ~2x the observed maximum, and the config-mirroring arrays
 * (`missing_fields`, `extracted_crm_fields`) are looser still because their length follows the
 * customer's configuration rather than the call. Lower one only against fresh
 * numbers — a cap below real output truncates silently, which is worse than the
 * runaway it prevents.
 *
 * `minItems` is deliberately absent everywhere: it would force the model to
 * invent entries to satisfy the grammar.
 */
export const PROMPT_BUILDER_SCHEMA = {
  name: 'prompt_builder_analysis',
  strict: true,
  schema: {
    type: 'object',
    properties: {
      // Summary Metrics
      quality_score: {
        type: 'number',
        description: 'Overall quality score (0-100)',
      },
      sale_probability: {
        type: 'number',
        // Bounded, not just described (#648): a 0-100 answer of "1" meaning 1 %
        // is indistinguishable from certainty, and the stored metric used to
        // guess the scale from the size of the number. Strict Structured
        // Outputs enforce the bound, so the scale is fixed by the contract.
        minimum: 0,
        maximum: 1,
        description:
          'Probability of sale as a FRACTION from 0 to 1 (0.55 means 55 %). Never a percent.',
      },
      attention_flag: {
        type: 'object',
        properties: {
          triggered: { type: 'boolean' },
          severity: {
            type: 'string',
            enum: ['critical', 'high', 'medium', 'low'],
          },
          reason: { type: 'string' },
        },
        required: ['triggered', 'severity', 'reason'],
        additionalProperties: false,
      },

      // Content Analysis
      topic: {
        type: 'string',
        description: 'Main topic of the conversation',
      },
      keywords: {
        type: 'array',
        maxItems: 30,
        items: { type: 'string' },
        description: 'Key terms and phrases',
      },
      key_moments: {
        type: 'array',
        maxItems: 30,
        items: {
          type: 'object',
          properties: {
            timestamp: {
              type: ['string', 'null'],
              description:
                'Model time hint in MM:SS/HH:MM:SS, or null when no reliable time exists; the system re-grounds it to diarization',
            },
            label: { type: 'string' },
            quote: { type: 'string' },
            importance: {
              type: 'string',
              enum: ['critical', 'high', 'medium', 'low'],
            },
            speaker: {
              type: 'string',
              enum: ['agent', 'customer', 'unknown'],
            },
            supports_attention_flag: {
              type: 'boolean',
              description:
                'true on the ONE moment whose quote proves the trigger named in attention_flag.reason; false on every other moment, and on all of them when attention_flag.triggered is false or the flag does not rest on a single utterance',
            },
          },
          required: [
            'timestamp',
            'label',
            'quote',
            'importance',
            'speaker',
            'supports_attention_flag',
          ],
          additionalProperties: false,
        },
        description: 'Important moments in the conversation',
      },
      summary: {
        type: 'string',
        description: 'Brief summary of the conversation',
      },
      structured_summary: {
        type: 'object',
        properties: {
          situation: { type: 'string' },
          actions: { type: 'array', maxItems: 20, items: { type: 'string' } },
          outcome: { type: 'string' },
        },
        required: ['situation', 'actions', 'outcome'],
        description: 'Structured summary (situation, actions, outcome)',
        additionalProperties: false,
      },

      // Speech Analysis
      filler_words: {
        type: 'object',
        properties: {
          count: { type: 'number' },
          examples: { type: 'array', maxItems: 20, items: { type: 'string' } },
        },
        required: ['count', 'examples'],
        description: 'Filler words analysis',
        additionalProperties: false,
      },
      stop_words: {
        type: 'object',
        properties: {
          detected: {
            type: 'array',
            maxItems: 50,
            items: {
              type: 'object',
              properties: {
                token: {
                  type: 'string',
                  description: 'The hedging word/phrase, verbatim',
                },
                speaker: {
                  type: 'string',
                  enum: ['agent', 'client', 'unknown'],
                  description: 'Who said it — only "agent" tokens count as agent hedging',
                },
                quote: {
                  type: 'string',
                  description:
                    'The verbatim sentence from the transcript containing the token',
                },
              },
              required: ['token', 'speaker', 'quote'],
              additionalProperties: false,
            },
          },
        },
        required: ['detected'],
        description:
          'Agent hedging words, each grounded with speaker and a verbatim quote',
        additionalProperties: false,
      },
      operator_errors: {
        type: 'array',
        maxItems: 20,
        items: {
          type: 'object',
          properties: {
            type: { type: 'string' },
            description: { type: 'string' },
            kind: {
              type: 'string',
              enum: ['said', 'omitted'],
              description:
                '"said" — the agent said or did something wrong in the call; "omitted" — the agent failed to do something',
            },
            evidence_quote: {
              type: 'string',
              description:
                'Verbatim from the transcript. For "said": the AGENT\'s own words. For "omitted": the line that called for the missing action (usually the customer\'s)',
            },
          },
          required: ['type', 'description', 'kind', 'evidence_quote'],
          additionalProperties: false,
        },
        description:
          'Agent/operator errors, each backed by a verbatim line: the agent\'s own for "said", the line that called for the action for "omitted" (#981)',
      },

      // Dialogue Technique
      greeting_intro: {
        type: 'object',
        properties: {
          score: { type: 'number' },
          feedback: { type: 'string' },
        },
        required: ['score', 'feedback'],
        description: 'Greeting quality',
        additionalProperties: false,
      },
      needs_qualification: {
        type: 'object',
        properties: {
          score: { type: ['number', 'null'] },
          identified_needs: { type: 'array', maxItems: 20, items: { type: 'string' } },
        },
        required: ['score', 'identified_needs'],
        description:
          'Needs identification. score=null when NO rubric component could sensibly ' +
          'apply to this call (a consumer renewing at a published price: nothing to ' +
          'budget, the customer IS the decision maker). This is NOT weak ' +
          'qualification — that scores on the rubric. The rubric and the field ' +
          'guideline both ask for null here, so the type must allow it: under ' +
          'strict structured output a plain `number` makes null grammatically ' +
          'impossible and the model falls back to 0, which reads as "did it badly".',
        additionalProperties: false,
      },
      objection_handling: {
        type: 'object',
        properties: {
          objections: {
            type: 'array',
            maxItems: 20,
            items: {
              type: 'object',
              properties: {
                type: {
                  type: 'string',
                  description:
                    'The customer objection as a short label (e.g. "price", "timing", "trust", "need to consult")',
                },
                score: {
                  type: 'number',
                  minimum: 0,
                  maximum: 100,
                  description:
                    'Rubric score for this individual objection on a 0-100 scale. The system derives handled and the aggregate from this value.',
                },
                quote: {
                  type: 'string',
                  description:
                    'Short verbatim transcript quote where the customer raised it, or empty string',
                },
                code: {
                  type: 'string',
                  enum: [...OBJECTION_CODES],
                  description:
                    'The ONE canonical reason behind this objection, chosen by meaning regardless of the conversation language. See the objection_handling guideline for the boundaries between codes. Use "other" only when no code fits.',
                },
                secondary_code: {
                  type: ['string', 'null'],
                  enum: [...OBJECTION_CODES, null],
                  description:
                    'A second, different canonical reason when the customer combined two in one objection (e.g. price AND payment terms); otherwise null.',
                },
              },
              required: ['type', 'score', 'quote', 'code', 'secondary_code'],
              additionalProperties: false,
            },
            description:
              'EVERY customer objection raised in the conversation, each independently scored with a supporting quote. Empty array when the customer raised none. The system derives handled from score >= 60.',
          },
        },
        required: ['objections'],
        description:
          'Each customer objection scored independently. The system derives handled flags and the overall score.',
        additionalProperties: false,
      },
      closing_techniques: {
        type: 'object',
        properties: {
          score: { type: ['number', 'null'] },
          techniques_used: { type: 'array', maxItems: 20, items: { type: 'string' } },
        },
        required: ['score', 'techniques_used'],
        description:
          'Closing quality. score=null (techniques_used=[]) when the call never reached ' +
          'a point where closing was possible — stale/wrong contact, customer disengaged ' +
          'during qualification, call disconnected within the first ~30 seconds. This is ' +
          'NOT a failed close (that scores 0 on the rubric below); it means there was ' +
          'nothing to close.',
        additionalProperties: false,
      },

      // Client sentiment only — numeric, with start/end tracking.
      // Agent (employee) emotional scoring is intentionally NOT collected:
      // inferring an employee's emotions in the workplace is prohibited under
      // the EU AI Act (Art. 5(1)(f)). Customer sentiment remains (allowed with
      // transparency under Art. 50).
      sentiment: {
        type: 'object',
        properties: {
          // Numeric values: -1.0 (very negative) to +1.0 (very positive)
          client_start: {
            type: 'number',
            description: 'Client sentiment at START of conversation (-1.0 to +1.0)',
          },
          client_end: {
            type: 'number',
            description: 'Client sentiment at END of conversation (-1.0 to +1.0)',
          },
          delta: {
            type: 'number',
            description: 'Change in client sentiment (client_end - client_start)',
          },
          overall: {
            type: 'number',
            description: 'Overall conversation sentiment (-1.0 to +1.0)',
          },
        },
        required: ['client_start', 'client_end', 'delta', 'overall'],
        description:
          'Client sentiment analysis with numeric values and start/end tracking',
        additionalProperties: false,
      },
      politeness: {
        type: 'object',
        properties: {
          score: { type: 'number' },
          polite_phrases: { type: 'array', maxItems: 20, items: { type: 'string' } },
        },
        required: ['score', 'polite_phrases'],
        description: 'Politeness analysis',
        additionalProperties: false,
      },
      interruptions: {
        type: 'object',
        properties: {
          count: { type: 'number' },
          by_agent: { type: 'number' },
          by_customer: { type: 'number' },
        },
        required: ['count', 'by_agent', 'by_customer'],
        description: 'Interruption analysis',
        additionalProperties: false,
      },
      clarity: {
        type: 'object',
        properties: {
          score: { type: 'number' },
          feedback: { type: 'string' },
        },
        required: ['score', 'feedback'],
        description: 'Clarity of communication',
        additionalProperties: false,
      },

      crm_completeness: {
        type: 'object',
        properties: {
          applicable: { type: 'boolean' },
          score: { type: ['number', 'null'] },
          missing_fields: { type: 'array', maxItems: 60, items: { type: 'string' } },
        },
        required: ['applicable', 'score', 'missing_fields'],
        description:
          'CRM data-entry completeness. applicable=false (score=null) when data collection was not a task of the call (known customer / existing linked deal).',
        additionalProperties: false,
      },
      // Business Intelligence — extracts competitive/market/product/compliance
      // signals for BI and product teams. Populated from organic mentions in
      // the transcript, not from structured CRM fields.
      business_intelligence: {
        type: 'object',
        properties: {
          competitor_mentions: {
            type: 'array',
            maxItems: 20,
            items: {
              type: 'object',
              properties: {
                name: {
                  type: 'string',
                  description: 'Competitor or brand name as mentioned',
                },
                context: {
                  type: 'string',
                  description: 'Short quote or paraphrase of the mention',
                },
                sentiment: {
                  type: 'string',
                  enum: ['positive', 'neutral', 'negative'],
                  description: 'How the speaker framed the competitor',
                },
              },
              required: ['name', 'context', 'sentiment'],
              additionalProperties: false,
            },
          },
          product_feedback: {
            type: 'array',
            maxItems: 20,
            items: {
              type: 'object',
              properties: {
                product: { type: 'string', description: 'Product or model named' },
                feedback: {
                  type: 'string',
                  description: 'The customer feedback verbatim or paraphrased',
                },
                severity: {
                  type: 'string',
                  enum: ['praise', 'minor_issue', 'major_issue', 'incident'],
                  description: 'Severity of the feedback signal',
                },
              },
              required: ['product', 'feedback', 'severity'],
              additionalProperties: false,
            },
          },
          market_signals: {
            type: 'array',
            maxItems: 20,
            items: {
              type: 'object',
              properties: {
                signal: {
                  type: 'string',
                  description: 'The market signal in one short phrase',
                },
                type: {
                  type: 'string',
                  enum: ['pricing', 'supply', 'regulation', 'trend', 'demand'],
                  description: 'Signal classification',
                },
              },
              required: ['signal', 'type'],
              additionalProperties: false,
            },
          },
          compliance_flags: {
            type: 'array',
            maxItems: 20,
            items: {
              type: 'object',
              properties: {
                flag: {
                  type: 'string',
                  description: 'Short label for the compliance concern',
                },
                context: {
                  type: 'string',
                  description: 'Why this was flagged (quote or paraphrase)',
                },
              },
              required: ['flag', 'context'],
              additionalProperties: false,
            },
          },
        },
        required: [
          'competitor_mentions',
          'product_feedback',
          'market_signals',
          'compliance_flags',
        ],
        description: 'Structured business intelligence extracted from the conversation',
        additionalProperties: false,
      },

      // Recommendations
      // Schema matches FIELDS_TO_TRANSLATE in prompt-builder:
      // - strengths[].behavior, strengths[].impact
      // - improvements[].behavior, improvements[].suggestion
      strengths_improvements: {
        type: 'object',
        properties: {
          strengths: {
            type: 'array',
            maxItems: 20,
            items: {
              type: 'object',
              properties: {
                behavior: { type: 'string', description: 'What the agent did well' },
                impact: {
                  type: 'string',
                  description: 'Positive impact of this behavior',
                },
              },
              required: ['behavior', 'impact'],
              additionalProperties: false,
            },
          },
          improvements: {
            type: 'array',
            maxItems: 20,
            items: {
              type: 'object',
              properties: {
                behavior: { type: 'string', description: 'What needs improvement' },
                suggestion: {
                  type: 'string',
                  description: 'How to improve this behavior',
                },
              },
              required: ['behavior', 'suggestion'],
              additionalProperties: false,
            },
          },
        },
        required: ['strengths', 'improvements'],
        description: 'Strengths and improvement areas with detailed behavior analysis',
        additionalProperties: false,
      },
      // Per-criterion coaching improvements, keyed to the rubric criteria — the
      // live coaching plan (WS7-T5). Distinct from strengths_improvements above
      // (free-text behavior/suggestion pairs shown in the analysis panel): this
      // is criterion-keyed so an agent's recurring gaps aggregate across calls.
      improvements: {
        type: 'array',
        // The only cap here whose ceiling is not the call but the RUBRIC: one
        // entry per scored criterion, so it grows when the criteria list does
        // (~25 today, observed max 6). Set well clear of that, because the
        // bound test asserts that a bound exists, not that it is big enough —
        // a 26th criterion would not fail anything, it would just silently
        // stop the model at the cap.
        maxItems: 40,
        items: {
          type: 'object',
          properties: {
            criterion_key: {
              type: 'string',
              description:
                'The rubric criterion this improvement targets. MUST be one of the criterion keys scored in this analysis (the same keys used for the dialogue/technique/communication sub-scores), so coaching aggregates by real criteria rather than free text.',
            },
            advice: {
              type: 'string',
              description:
                'Specific, actionable coaching advice for this criterion, grounded in what actually happened on this call',
            },
            priority: {
              type: 'string',
              enum: ['low', 'medium', 'high'],
              description: 'How urgently the agent should work on this criterion',
            },
            example_quote: {
              type: 'string',
              description:
                'Short verbatim transcript quote illustrating the issue, or empty string',
            },
          },
          required: ['criterion_key', 'advice', 'priority', 'example_quote'],
          additionalProperties: false,
        },
        description:
          'Per-criterion coaching improvements the agent should act on, keyed to the rubric criteria. Drives the live coaching plan; empty array when the call needs no coaching.',
      },
      extracted_crm_fields: {
        type: 'array',
        maxItems: 60,
        items: {
          type: 'object',
          properties: {
            field_id: {
              type: 'string',
              description:
                'CRM field identifier exactly as provided in <target_crm_fields> (e.g. UF_CRM_123)',
            },
            value: {
              type: 'string',
              description:
                'Value extracted from the conversation, formatted for the field type (enum: use the exact option label; date: ISO 8601)',
            },
            confidence: {
              type: 'number',
              description: 'Extraction confidence 0..1',
            },
            evidence_quote: {
              type: 'string',
              description: 'Short verbatim transcript quote supporting the value',
            },
          },
          required: ['field_id', 'value', 'confidence', 'evidence_quote'],
          additionalProperties: false,
        },
        description:
          'Values extracted from the conversation for the operator-selected CRM fields listed in <target_crm_fields>. Only include fields with clear evidence.',
      },
      agreements: {
        type: 'array',
        maxItems: 30,
        items: {
          type: 'object',
          properties: {
            type: {
              type: 'string',
              enum: ['call', 'meeting', 'email', 'task'],
              description: 'Kind of follow-up agreed in the call',
            },
            subject: { type: 'string', description: 'Short title for the activity' },
            description: {
              type: 'string',
              description: 'What was agreed, in one or two sentences',
            },
            due_hint: {
              type: 'string',
              description:
                'Relative timing phrase as spoken (e.g. "завтра", "в понедельник")',
            },
            due_at: {
              type: 'string',
              description:
                'Concrete absolute due date in ISO 8601 (YYYY-MM-DD or a full datetime), anchored to the call time. ALWAYS resolve a concrete date whenever ANY timing is mentioned, including vague periods — map a period to its boundary date (e.g. "до конца недели" -> that Friday; "до конца лета" -> Aug 31; "в начале следующего месяца" -> 1st of next month; "через пару дней" -> call date + 2 days). Empty string ONLY when no timing was mentioned at all.',
            },
            responsible_hint: {
              type: 'string',
              description: 'Who should do it as mentioned, or empty string',
            },
            confidence: {
              type: 'number',
              description: 'Confidence 0..1 that this is a real commitment',
            },
            evidence_quote: {
              type: 'string',
              description: 'Short verbatim transcript quote',
            },
            evidence_timestamp: {
              type: 'string',
              description:
                'Transcript timecode where evidence_quote was spoken, in MM:SS or HH:MM:SS format',
            },
          },
          required: [
            'type',
            'subject',
            'description',
            'due_hint',
            'due_at',
            'responsible_hint',
            'confidence',
            'evidence_quote',
            'evidence_timestamp',
          ],
          additionalProperties: false,
        },
        description:
          'Concrete follow-up commitments made during the call (callbacks, send-email, meetings, tasks).',
      },
    },
    required: [
      'quality_score',
      'sale_probability',
      'attention_flag',
      'topic',
      'keywords',
      'key_moments',
      'summary',
      'structured_summary',
      'filler_words',
      'stop_words',
      'operator_errors',
      'greeting_intro',
      'needs_qualification',
      'objection_handling',
      'closing_techniques',
      'sentiment',
      'politeness',
      'interruptions',
      'clarity',
      'crm_completeness',
      'business_intelligence',
      'strengths_improvements',
      'improvements',
      'extracted_crm_fields',
      'agreements',
    ],
    additionalProperties: false,
  },
} as const;

/**
 * Toggle → schema field mapping (camelCase toggle key → snake_case field name).
 * Single source of truth: consumed by buildAnalysisSchema here, by the analyze
 * worker's null-loop, and by @aica/prompt-builder output-format.ts. Adding a new
 * toggle requires editing ONLY this array.
 * call_category is always required and has no toggle.
 */
export const TOGGLE_FIELD_MAP = [
  ['qualityScore', 'quality_score'],
  ['saleProbability', 'sale_probability'],
  ['attentionFlag', 'attention_flag'],
  ['topic', 'topic'],
  ['keywords', 'keywords'],
  ['keyMoments', 'key_moments'],
  ['summary', 'summary'],
  ['structuredSummary', 'structured_summary'],
  ['fillerWords', 'filler_words'],
  ['stopWords', 'stop_words'],
  ['operatorErrors', 'operator_errors'],
  ['greetingIntro', 'greeting_intro'],
  ['needsQualification', 'needs_qualification'],
  ['objectionHandling', 'objection_handling'],
  ['closingTechniques', 'closing_techniques'],
  ['sentiment', 'sentiment'],
  ['politeness', 'politeness'],
  ['interruptions', 'interruptions'],
  ['clarity', 'clarity'],
  ['crmCompleteness', 'crm_completeness'],
  ['businessIntelligence', 'business_intelligence'],
  ['strengthsImprovements', 'strengths_improvements'],
  ['extractedCrmFields', 'extracted_crm_fields'],
  ['agreements', 'agreements'],
] as const;

export interface AnalysisSchemaContext {
  /** LLM call category, when categorize-step has already run. */
  callCategory?: string;
  /** Record channel. Defaults from the base profile category. */
  channel?: string;
}

// Local lookup over the shared purchase-intent list (@aica/api-types) — see
// PURCHASE_INTENT_CATEGORIES' own doc comment for why this list is shared
// and when to stop sharing it.
const SALES_ANALYSIS_CATEGORY_SET: ReadonlySet<string> = new Set(
  PURCHASE_INTENT_CATEGORIES
);

function isSalesAnalysis(category: string, callCategory?: string): boolean {
  return callCategory
    ? SALES_ANALYSIS_CATEGORY_SET.has(callCategory)
    : category === 'sales-call' || category === 'sales-chat';
}

/**
 * Критерии, физически неприменимые к текстовому каналу.
 *
 * Ровно этими полями DEFAULT_CHAT_TOGGLES отличается от звонковых
 * дефолтов (@aica/prompt-builder). Знание живёт здесь, в коде, а не в данных
 * отдельного «чатового» профиля. Дрейф между двумя источниками ловит
 * packages/prompt-builder/test/channel-toggles-drift.test.ts.
 */
export const CHAT_UNSUPPORTED_TOGGLES = [
  'keyMoments', // у чата нет таймкодов
  'fillerWords', // слова-паразиты — свойство речи
  'interruptions', // перебивания — свойство речи
  'clarity', // дикция — свойство речи
] as const;

/**
 * Погасить критерии, неприменимые к каналу записи.
 *
 * Канал — вторая ось анализа: категория решает, ЧТО оценивать, канал — КАК и
 * что оценивать бессмысленно. Вызывать ДО построения промпта, JSON-схемы и
 * обнуления отключённых полей — все три должны видеть один и тот же набор.
 */
export function resolveChannelToggles<T extends Record<string, boolean | undefined>>(
  toggles: T,
  channel: AnalysisSchemaContext['channel']
): T {
  // Always return a copy — the call path used to return the SAME reference,
  // making copy semantics depend on the argument's value. One caller
  // (analyze/worker.ts) defended against that by spreading on its own side;
  // preview.ts didn't, and relied on this function copying. Copying
  // unconditionally makes the contract hold regardless of channel, and lets
  // every caller drop its own defensive spread.
  if (channel !== 'chat') return { ...toggles };

  const masked: T = { ...toggles };
  for (const key of CHAT_UNSUPPORTED_TOGGLES) {
    (masked as Record<string, boolean>)[key] = false;
  }
  return masked;
}

/**
 * `supports_attention_flag` links a key moment to the attention flag, so it
 * exists only when both `attentionFlag` and `keyMoments` are on. The base
 * constant carries it; this variant drops it from the element's properties and
 * `required` when `attentionFlag` is off (a key the prompt never explains must
 * not be demanded from the model under strict mode).
 */
function buildKeyMomentsSchema(
  toggles: Record<string, boolean | undefined>
): Record<string, unknown> {
  const keyMoments = PROMPT_BUILDER_SCHEMA.schema.properties.key_moments as unknown as {
    items: {
      properties: Record<string, unknown>;
      required: readonly string[];
    } & Record<string, unknown>;
  } & Record<string, unknown>;
  if (toggles.attentionFlag) return keyMoments;

  const { supports_attention_flag: _omitted, ...properties } =
    keyMoments.items.properties;
  return {
    ...keyMoments,
    items: {
      ...keyMoments.items,
      properties,
      required: keyMoments.items.required.filter(
        (field) => field !== 'supports_attention_flag'
      ),
    },
  };
}

/**
 * Build a dynamic analysis JSON Schema based on active prompt toggles.
 *
 * Disabled toggles get `{ type: 'null' }` in the schema, which forces the LLM
 * to return null for those fields via OpenAI structured output enforcement.
 * This prevents the LLM from spending tokens analyzing disabled features.
 *
 * @param toggles - Active prompt toggles from the prompt profile
 * @param category - Prompt category ('sales-call', 'sales-chat', etc.)
 * @returns JSON Schema object suitable for LlmAnalyzeInput.json_schema
 */
export function buildAnalysisSchema(
  toggles: Record<string, boolean | undefined>,
  category: string,
  context: AnalysisSchemaContext = {}
): Record<string, unknown> {
  const isSales = isSalesAnalysis(category, context.callCategory);

  // Collect fields that must be null
  const nullFields = new Set<string>();
  for (const [toggle, field] of TOGGLE_FIELD_MAP) {
    if (!toggles[toggle]) {
      nullFields.add(field);
    }
  }
  // sale_probability is null for non-sales categories even when toggle is on
  if (!isSales) {
    nullFields.add('sale_probability');
  }
  // Build property map: active fields keep full schema, disabled are excluded entirely
  // (avoids Anthropic's 16-union-type limit — excluded fields produce 0 unions)
  const baseProperties = PROMPT_BUILDER_SCHEMA.schema.properties as Record<
    string,
    unknown
  >;
  const properties: Record<string, unknown> = {};
  for (const [key, propSchema] of Object.entries(baseProperties)) {
    if (!nullFields.has(key)) {
      properties[key] =
        key === 'key_moments' ? buildKeyMomentsSchema(toggles) : propSchema;
    }
  }

  return {
    name: PROMPT_BUILDER_SCHEMA.name,
    strict: true,
    schema: {
      type: 'object',
      properties,
      required: ([...PROMPT_BUILDER_SCHEMA.schema.required] as string[]).filter(
        (field) => !nullFields.has(field)
      ),
      additionalProperties: false,
    },
  };
}
